// src/lib/supabaseClient.js
import { createClient } from '@supabase/supabase-js';  
import validateEnvironment from '../config/validateEnv';

// Validate environment variables  
const isValid = validateEnvironment();

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;  
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {  
  console.error('Supabase environment variables are missing. Please check your .env file.');  
}

// Create supabase client with enhanced error handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {  
  auth: {  
    persistSession: true,  
    autoRefreshToken: true  
  }  
});

// Log warning if validation failed but we're still creating the client  
if (!isValid) {  
  console.warn('Supabase client initialized with potentially invalid configuration.');  
}

// Test connection to verify it's working
const testConnection = async () => {
  try {
    const { error } = await supabase.from('business_profiles').select('count', { count: 'exact', head: true });
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Supabase connection test failed:', error);
    return false;
  }
};

// Function to validate database schema
export const validateDatabaseSchema = async () => {
  try {
    // Check if business_profiles table exists
    // eslint-disable-next-line no-unused-vars
    const { data, error: profileError } = await supabase
      .from('business_profiles')
      .select('*')
      .limit(1);
    
    if (profileError) {
      console.error("Error accessing business_profiles table:", profileError);
      return false;
    }
        
    // Check if offers table exists
    // eslint-disable-next-line no-unused-vars
    const { data: offersData, error: offersError } = await supabase
      .from('offers')
      .select('*')
      .limit(1);
    
    if (offersError) {
      console.error("Error accessing offers table:", offersError);
      return false;
    }
    
    // RLS test removed - it was using an invalid query that caused 400 errors
    // The string 'current_user' is not valid in JavaScript context
    
    return true;
  } catch (error) {
    console.error("Database schema validation failed:", error);
    return false;
  }
};

// Test user permissions
export const testUserPermissions = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    console.error("No authenticated user found");
    return false;
  }
    
  try {
    // First, check if a profile already exists
    const { data: existingProfile, error: checkError } = await supabase
      .from('business_profiles')
      .select('*')
      .eq('user_id', user.id)
      .single();
      
    if (checkError && checkError.code !== 'PGRST116') {
      console.error("Error checking for existing profile:", checkError);
      return false;
    }
    
    // Test ability to update if profile exists, or insert if it doesn't
    const testData = {
      user_id: user.id,
      business_name: "TEST_PERMISSION_" + Date.now(),
      industry: "Test",
      updated_at: new Date().toISOString()
    };
    
    if (existingProfile) {
      
      // Test update - removed the unused updateData variable
      const { error: updateError } = await supabase
        .from('business_profiles')
        .update({
          industry: testData.industry,
          updated_at: testData.updated_at
        })
        .eq('user_id', user.id)
        .select();
        
      if (updateError) {
        console.error("Update permission test failed:", updateError);
        return false;
      }
            
      // Restore original data
      await supabase
        .from('business_profiles')
        .update({
          industry: existingProfile.industry,
          updated_at: existingProfile.updated_at
        })
        .eq('user_id', user.id);
        
    } else {
      
      // Test insert - removed the unused insertData variable
      const { error: insertError } = await supabase
        .from('business_profiles')
        .insert(testData)
        .select();
        
      if (insertError) {
        console.error("Insert permission test failed:", insertError);
        return false;
      }
            
      // Clean up test data
      const { error: deleteError } = await supabase
        .from('business_profiles')
        .delete()
        .eq('business_name', testData.business_name);
        
      if (deleteError) {
        console.warn("Delete test data failed (permissions may be restricted):", deleteError);
      } else {
        // Delete succeeded
      }
    }
    
    return true;
  } catch (error) {
    console.error("Permission test failed with exception:", error);
    return false;
  }
};

// Initialize tests
// Run connection test and log result
testConnection();

// Add to the startup sequence
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN' && session?.user) {
    setTimeout(() => {
      testUserPermissions();
    }, 2000);
  }
});

// Schedule schema validation
setTimeout(() => {
  validateDatabaseSchema();
}, 1000);